import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { Menu, X, User, LogOut, Facebook, Twitter, Linkedin } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { mockUser, BLOOD_TYPES, ORGANS } from '../mockData';
import { toast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';
import { prepareForUpdate } from '../services/userService';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

const Layout = () => {
  const { user, isAuthenticated, signOut } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showDonorDialog, setShowDonorDialog] = useState(false);
  const [selectedOrgans, setSelectedOrgans] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(true);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const mobileMenuRef = useRef(null);
  const menuButtonRef = useRef(null);

  // Glassmorphism header on scroll
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Focus trap & Escape key for mobile menu
  const closeMobileMenu = useCallback(() => {
    setMobileMenuOpen(false);
    menuButtonRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!mobileMenuOpen) return;
    // Move focus into the menu when it opens
    const firstFocusable = mobileMenuRef.current?.querySelector(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    firstFocusable?.focus();

    // Escape key closes menu
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') closeMobileMenu();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [mobileMenuOpen, closeMobileMenu]);

  useEffect(() => { /* countries fetch disabled */ }, []);

  const fetchCountries = async () => {
    try {
      const response = await axios.get(`${API}/regulations`);
      // Sort countries alphabetically by name
      const sortedCountries = response.data.sort((a, b) => a.name.localeCompare(b.name));
      setCountries(sortedCountries);
      setLoadingCountries(false);
    } catch (error) {
      setLoadingCountries(false);
    }
  };

  const handleOrganToggle = (organId) => {
    setSelectedOrgans(prev =>
      prev.includes(organId)
        ? prev.filter(id => id !== organId)
        : [...prev, organId]
    );
  };

  const handleDonorRegistration = async (e, donationType) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    try {
      const email = formData.get(`${donationType}-email`);
      if (!email) {
        toast({
          title: 'Email Required',
          description: 'Please enter your email address',
          variant: 'destructive',
        });
        return;
      }

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        toast({
          title: 'Invalid Email',
          description: 'Please enter a valid email address',
          variant: 'destructive',
        });
        return;
      }

      // Validate required fields
      const name = formData.get(`${donationType}-name`);
      const country = formData.get(`${donationType}-country`);
      const city = formData.get(`${donationType}-city`);
      const zipCode = formData.get(`${donationType}-zip-code`);
      const phone = formData.get(`${donationType}-phone`);

      if (!name || !country || !city || !zipCode || !phone) {
        toast({
          title: 'Missing Required Fields',
          description: 'Please fill in all required fields (name, country, city, ZIP code, and phone)',
          variant: 'destructive',
        });
        return;
      }

      // Build user data in camelCase first
      const userDataCamel = {
        email: email,
        name: name,
        bloodType: formData.get(`${donationType}-blood-type`) || formData.get('organ-blood-type'),
        country: country,
        city: city,
        zipCode: zipCode,
        phone: phone
      };

      // For blood donation
      if (donationType === 'blood') {
        userDataCamel.isBloodDonor = true;
      }
      
      // For organ donation
      if (donationType === 'organ') {
        userDataCamel.isOrganDonor = true;
        userDataCamel.organsPledged = selectedOrgans;
        userDataCamel.bodyDonation = formData.get('body-pledge') === 'on';
        userDataCamel.emergencyContact = {
          name: formData.get('emergency-contact-name') || '',
          phone: formData.get('emergency-contact-phone') || '',
          relationship: 'Emergency Contact'
        };
      }

      // Transform to backend format (snake_case)
      const userData = prepareForUpdate(userDataCamel);

      await axios.post(`${API}/users`, userData);
      
      toast({
        title: 'Registration Successful!',
        description: 'Check your email for a confirmation link to complete your registration and set up your password.',
      });
      setShowDonorDialog(false);
      setSelectedOrgans([]);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Registration failed. Please try again.';
      toast({
        title: 'Registration Error',
        description: errorMessage,
        variant: 'destructive',
      });
      setShowDonorDialog(false);
      setSelectedOrgans([]);
    }
  };

  const navigation = [
    { name: 'Home', path: '/' },
    { name: 'Blood Requests', path: '/blood-requests' },
    { name: 'Donation Centers', path: '/donation-centers' },
    { name: 'Organ Donation', path: '/organ-donation' },
    { name: 'Regulations', path: '/regulations' },
    ...(user?.role === 'admin' ? [{ name: 'Admin', path: '/admin' }] : []),
    { name: 'Partners', path: '/partners' }
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const currentYear = new Date().getFullYear();
  const footerYear = currentYear > 2025 ? `2025-${currentYear}` : '2025';

  return (
    <div className="min-h-screen flex flex-col">
      {/* Skip to main content link for accessibility */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 bg-red-700 text-white px-4 py-2 rounded-md z-50"
      >
        Skip to main content
      </a>

      {/* Header */}
      <header
        role="banner"
        className={`text-gray-900 sticky top-0 z-40 transition-all duration-300 ${
          scrolled ? 'glass shadow-lg' : 'bg-white border-b border-gray-100 shadow-sm'
        }`}
      >
        <nav role="navigation" aria-label="Main navigation" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2 hover:opacity-80 transition-opacity" aria-label="DonorsHouse — Home">
              <img 
                src="/donorshouse-transparent-1000.png" 
                alt="DonorsHouse"
                width="160"
                height="64"
                className="h-14 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-1">
              {navigation.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  aria-current={isActive(item.path) ? 'page' : undefined}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive(item.path)
                      ? 'bg-red-700 text-white'
                      : 'text-gray-800 hover:bg-gray-200 hover:text-gray-900'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
            </div>

            {/* Desktop Auth Buttons */}
            <div className="hidden md:flex items-center space-x-3">
              {/* Become a Donor - Only visible when not authenticated */}
              {!isAuthenticated && (
                <Link to="/register">
                  <Button
                    size="sm"
                    className="bg-red-700 hover:bg-red-900 text-white font-medium btn-transition"
                  >
                    Become a Donor
                  </Button>
                </Link>
              )}
              {/* Keep Dialog for backward compatibility but hide it */}
              <Dialog open={showDonorDialog} onOpenChange={setShowDonorDialog} style={{ display: 'none' }}>
                <DialogTrigger asChild>
                  <div style={{ display: 'none' }}></div>
                </DialogTrigger>
                  <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="text-gray-900">Become a Donor</DialogTitle>
                    </DialogHeader>
                    <Tabs defaultValue="blood" className="w-full">
                      <TabsList className="grid w-full grid-cols-2 bg-gray-200">
                        <TabsTrigger value="blood" className="data-[state=active]:bg-red-700 data-[state=active]:text-white">Blood Donation</TabsTrigger>
                        <TabsTrigger value="organ" className="data-[state=active]:bg-red-700 data-[state=active]:text-white">Organ Pledge</TabsTrigger>
                      </TabsList>
                      <TabsContent value="blood">
                        <form onSubmit={(e) => handleDonorRegistration(e, 'blood')} className="space-y-4">
                          <div>
                            <Label htmlFor="blood-email" className="text-gray-900">Email Address *</Label>
                            <Input 
                              id="blood-email" 
                              name="blood-email" 
                              type="email" 
                              placeholder="your.email@example.com" 
                              required 
                              className="border-gray-300" 
                            />
                            <p className="text-xs text-gray-600 mt-1">We'll send you a confirmation email</p>
                          </div>
                          <div>
                            <Label htmlFor="blood-name" className="text-gray-900">Full Name *</Label>
                            <Input id="blood-name" name="blood-name" placeholder="Your name" required className="border-gray-300" />
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                              <Label htmlFor="blood-blood-type" className="text-gray-900">Blood Type *</Label>
                              <Select name="blood-blood-type" required>
                                <SelectTrigger className="border-gray-300">
                                  <SelectValue placeholder="Select" />
                                </SelectTrigger>
                                <SelectContent>
                                  {BLOOD_TYPES.map(type => (
                                    <SelectItem key={type} value={type}>{type}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label htmlFor="blood-country" className="text-gray-900">Country *</Label>
                              <Select name="blood-country" required disabled={loadingCountries}>
                                <SelectTrigger className="border-gray-300">
                                  <SelectValue placeholder={loadingCountries ? "Loading countries..." : "Select country"} />
                                </SelectTrigger>
                                <SelectContent>
                                  {countries.map(country => (
                                    <SelectItem key={country.code} value={country.name}>{country.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          <div>
                            <Label htmlFor="blood-city" className="text-gray-900">City</Label>
                            <Input id="blood-city" name="blood-city" placeholder="Your city" required className="border-gray-300" />
                          </div>
                          <div>
                            <Label htmlFor="blood-zip-code" className="text-gray-900">ZIP/Postal Code</Label>
                            <Input id="blood-zip-code" name="blood-zip-code" placeholder="12345" required className="border-gray-300" />
                          </div>
                          <div>
                            <Label htmlFor="blood-phone" className="text-gray-900">Phone Number</Label>
                            <Input id="blood-phone" name="blood-phone" type="tel" placeholder="+1 234 567 8900" required className="border-gray-300" />
                          </div>
                          <div className="flex items-center space-x-2">
                            <Checkbox id="emergency-contact" name="emergency-contact" />
                            <Label htmlFor="emergency-contact" className="text-gray-900 font-normal cursor-pointer">
                              I&apos;m willing to be contacted for emergency blood requests in my area
                            </Label>
                          </div>
                          <Button type="submit" className="w-full bg-red-700 hover:bg-red-900 text-white">
                            Complete Registration
                          </Button>
                        </form>
                      </TabsContent>
                      <TabsContent value="organ">
                        <form onSubmit={(e) => handleDonorRegistration(e, 'organ')} className="space-y-4">
                          <div>
                            <Label htmlFor="organ-email" className="text-gray-900">Email Address *</Label>
                            <Input 
                              id="organ-email" 
                              name="organ-email" 
                              type="email" 
                              placeholder="your.email@example.com" 
                              required 
                              className="border-gray-300" 
                            />
                            <p className="text-xs text-gray-600 mt-1">We'll send you a confirmation email</p>
                          </div>
                          <div>
                            <Label htmlFor="organ-name" className="text-gray-900">Full Name *</Label>
                            <Input id="organ-name" name="organ-name" placeholder="Your name" required className="border-gray-300" />
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                              <Label htmlFor="organ-blood-type" className="text-gray-900">Blood Type</Label>
                              <Select name="organ-blood-type" required>
                                <SelectTrigger className="border-gray-300">
                                  <SelectValue placeholder="Select" />
                                </SelectTrigger>
                                <SelectContent>
                                  {BLOOD_TYPES.map(type => (
                                    <SelectItem key={type} value={type}>{type}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label htmlFor="organ-country" className="text-gray-900">Country *</Label>
                              <Select name="organ-country" required disabled={loadingCountries}>
                                <SelectTrigger className="border-gray-300">
                                  <SelectValue placeholder={loadingCountries ? "Loading countries..." : "Select country"} />
                                </SelectTrigger>
                                <SelectContent>
                                  {countries.map(country => (
                                    <SelectItem key={country.code} value={country.name}>{country.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          <div>
                            <Label htmlFor="organ-city" className="text-gray-900">City</Label>
                            <Input id="organ-city" name="organ-city" placeholder="Your city" required className="border-gray-300" />
                          </div>
                          <div>
                            <Label htmlFor="organ-zip-code" className="text-gray-900">ZIP/Postal Code</Label>
                            <Input id="organ-zip-code" name="organ-zip-code" placeholder="12345" required className="border-gray-300" />
                          </div>
                          <div>
                            <Label htmlFor="donation-type" className="text-gray-900">Donation Type</Label>
                            <Select name="donation-type" required>
                              <SelectTrigger className="border-gray-300">
                                <SelectValue placeholder="Select" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="living">Living</SelectItem>
                                <SelectItem value="deceased">Deceased</SelectItem>
                                <SelectItem value="both">Both</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-gray-900 mb-3 block">Organs to Pledge</Label>
                            <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-300 rounded-md p-3">
                              {ORGANS.map((organ) => (
                                <div key={organ.id} className="flex items-start space-x-3">
                                  <Checkbox
                                    id={`organ-pledge-${organ.id}`}
                                    checked={selectedOrgans.includes(organ.id)}
                                    onCheckedChange={() => handleOrganToggle(organ.id)}
                                  />
                                  <div className="flex-1">
                                    <Label htmlFor={`organ-pledge-${organ.id}`} className="text-gray-900 font-medium cursor-pointer">
                                      {organ.name}
                                    </Label>
                                    {organ.livingDonation && (
                                      <p className="text-xs text-gray-600">Living donation possible</p>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div className="border border-gray-300 rounded-md p-4 bg-gray-50">
                            <div className="flex items-start space-x-3">
                              <Checkbox id="body-pledge" name="body-pledge" />
                              <div>
                                <Label htmlFor="body-pledge" className="text-gray-900 font-semibold cursor-pointer">
                                  Pledge Entire Body for Medical Science
                                </Label>
                                <p className="text-sm text-gray-700 mt-1">
                                  After death, donate your body to medical schools and research institutions.
                                </p>
                              </div>
                            </div>
                          </div>
                          <div>
                            <Label htmlFor="emergency-contact-name" className="text-gray-900">Emergency Contact Name</Label>
                            <Input id="emergency-contact-name" name="emergency-contact-name" placeholder="Contact person name" className="border-gray-300" />
                          </div>
                          <div>
                            <Label htmlFor="emergency-contact-phone" className="text-gray-900">Emergency Contact Phone</Label>
                            <Input id="emergency-contact-phone" name="emergency-contact-phone" type="tel" placeholder="+1 234 567 8900" className="border-gray-300" />
                          </div>
                          <Button type="submit" className="w-full bg-red-700 hover:bg-red-900 text-white">
                            Complete Registration
                          </Button>
                        </form>
                      </TabsContent>
                    </Tabs>
                  </DialogContent>
                </Dialog>

              {/* Sign In button - when not logged in */}
              {!isAuthenticated && (
                <Link to="/signin">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-gray-800 hover:bg-gray-200 btn-transition"
                  >
                    Sign In
                  </Button>
                </Link>
              )}

              {/* Profile and Logout buttons - only when logged in */}
              {isAuthenticated && (
                <>
                  <Link to="/profile">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-gray-800 hover:bg-gray-200 hover:text-gray-900 btn-transition"
                    >
                      <User className="h-4 w-4 mr-2" />
                      {user?.name?.split(' ')[0] || 'Profile'}
                    </Button>
                  </Link>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-gray-800 hover:bg-gray-200 btn-transition"
                    onClick={signOut}
                  >
                    <LogOut className="h-4 w-4" />
                  </Button>
                </>
              )}
            </div>

            {/* Mobile menu button */}
            <button
              type="button"
              ref={menuButtonRef}
              className="md:hidden p-2 rounded-md text-gray-800 hover:text-gray-900 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-red-600"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-expanded={mobileMenuOpen}
              aria-controls="mobile-nav"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? (
                <X className="h-6 w-6" aria-hidden="true" />
              ) : (
                <Menu className="h-6 w-6" aria-hidden="true" />
              )}
            </button>
          </div>
        </nav>

        {/* Mobile menu — full accessibility: role=dialog, aria-modal, focus trap */}
        {mobileMenuOpen && (
          <div
            id="mobile-nav"
            ref={mobileMenuRef}
            role="dialog"
            aria-modal="true"
            aria-label="Navigation menu"
            className="md:hidden border-t border-gray-200 bg-white shadow-lg animate-fade-in"
          >
            <div className="px-2 pt-2 pb-4 space-y-1">
              {navigation.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  aria-current={isActive(item.path) ? 'page' : undefined}
                  className={`block px-3 py-3 rounded-lg text-base font-medium transition-colors ${
                    isActive(item.path)
                      ? 'bg-red-700 text-white'
                      : 'text-gray-800 hover:bg-red-50 hover:text-red-700'
                  }`}
                  onClick={() => closeMobileMenu()}
                >
                  {item.name}
                </Link>
              ))}
              {!isAuthenticated && (
                <Link
                  to="/register"
                  className="block w-full text-left px-3 py-3 rounded-lg text-base font-medium bg-red-700 text-white hover:bg-red-800 transition-colors"
                  onClick={() => closeMobileMenu()}
                >
                  Become a Donor
                </Link>
              )}
              {!isAuthenticated && (
                <Link
                  to="/signin"
                  className="block px-3 py-3 rounded-lg text-base font-medium text-gray-800 hover:bg-gray-100 hover:text-gray-900 transition-colors"
                  onClick={() => closeMobileMenu()}
                >
                  Sign In
                </Link>
              )}
              {isAuthenticated && (
                <>
                  <Link
                    to="/profile"
                    className="block px-3 py-3 rounded-lg text-base font-medium text-gray-800 hover:bg-gray-100 hover:text-gray-900 transition-colors"
                    onClick={() => closeMobileMenu()}
                  >
                    <User className="h-4 w-4 inline mr-2" aria-hidden="true" />
                    Profile
                  </Link>
                  <button
                    className="w-full text-left px-3 py-3 rounded-lg text-base font-medium text-gray-800 hover:bg-gray-100 hover:text-gray-900 transition-colors"
                    onClick={() => {
                      signOut();
                      closeMobileMenu();
                    }}
                  >
                    <LogOut className="h-4 w-4 inline mr-2" aria-hidden="true" />
                    Sign Out
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main id="main-content" role="main" className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer role="contentinfo" aria-label="Site footer" className="bg-white text-gray-700 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* New Abilities Foundation Support Banner */}
          <div className="mb-8 p-6 bg-gradient-to-r from-red-50 to-red-100 rounded-xl border border-red-200">
            <div className="text-center">
              <p className="text-red-900 font-semibold mb-1">This initiative is supported by</p>
              <p className="text-xl font-bold text-gray-900 mb-1">New Abilities Foundation</p>
              <p className="text-gray-600 text-sm mb-4">A 501(c)(3) nonprofit organization dedicated to global health</p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a
                  href="https://newabilities.org"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Visit New Abilities Foundation — opens in new tab"
                  className="inline-block px-6 py-2 bg-gray-900 text-white rounded-lg font-medium hover:bg-gray-800 btn-transition"
                >
                  Visit Website
                </a>
                <a
                  href="https://newabilities.org/donate"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Donate to New Abilities Foundation — opens in new tab"
                  className="inline-block px-6 py-2 bg-red-700 text-white rounded-lg font-medium hover:bg-red-800 btn-transition"
                >
                  Donate Now
                </a>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* About */}
            <div>
              <div className="mb-4">
                <img 
                  src="/donorshouse-transparent-1000.png" 
                  alt="DonorsHouse"
                  width="200"
                  height="80"
                  className="h-20 w-auto"
                  loading="lazy"
                />
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Connecting donors and recipients across the globe. Your donation can save lives.
              </p>
              {/* Social Links */}
              <div className="flex items-center gap-3">
                <a
                  href="https://twitter.com/donorshouse"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Follow DonorsHouse on X (Twitter) — opens in new tab"
                  className="p-2 rounded-full bg-gray-100 hover:bg-red-100 hover:text-red-700 text-gray-500 transition-colors"
                >
                  <Twitter className="h-4 w-4" aria-hidden="true" />
                </a>
                <a
                  href="https://facebook.com/donorshouse"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Follow DonorsHouse on Facebook — opens in new tab"
                  className="p-2 rounded-full bg-gray-100 hover:bg-red-100 hover:text-red-700 text-gray-500 transition-colors"
                >
                  <Facebook className="h-4 w-4" aria-hidden="true" />
                </a>
                <a
                  href="https://linkedin.com/company/donorshouse"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Connect with DonorsHouse on LinkedIn — opens in new tab"
                  className="p-2 rounded-full bg-gray-100 hover:bg-red-100 hover:text-red-700 text-gray-500 transition-colors"
                >
                  <Linkedin className="h-4 w-4" aria-hidden="true" />
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-gray-900 font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2 text-sm">
                <li><Link to="/" className="hover:text-gray-900 transition-colors">Home</Link></li>
                <li><Link to="/blood-requests" className="hover:text-gray-900 transition-colors">Blood Requests</Link></li>
                <li><Link to="/donation-centers" className="hover:text-gray-900 transition-colors">Donation Centers</Link></li>
                <li><Link to="/global-map" className="hover:text-gray-900 transition-colors">Global Map</Link></li>
              </ul>
            </div>

            {/* For Donors */}
            <div>
              <h3 className="text-gray-900 font-semibold mb-4">For Donors</h3>
              <ul className="space-y-2 text-sm">
                {!isAuthenticated && (
                  <li>
                    <button
                      onClick={() => {
                        setShowDonorDialog(true);
                      }}
                      className="hover:text-gray-900 transition-colors text-left"
                    >
                      Register as Donor
                    </button>
                  </li>
                )}
                <li><Link to="/organ-donation" className="hover:text-gray-900 transition-colors">Organ Donation</Link></li>
                <li><Link to="/regulations" className="hover:text-gray-900 transition-colors">Regulations</Link></li>
                <li><Link to="/badge" className="hover:text-gray-900 transition-colors">Digital Badge</Link></li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h3 className="text-gray-900 font-semibold mb-4">Legal</h3>
              <ul className="space-y-2 text-sm">
                <li><Link to="/privacy" className="hover:text-gray-900 transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-gray-900 transition-colors">Terms of Use</Link></li>
                <li><Link to="/accessibility" className="hover:text-gray-900 transition-colors">Accessibility</Link></li>
              </ul>
            </div>
          </div>

          <div className="mt-8 pt-8 border-t border-gray-200">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-gray-600">
              <p className="text-center sm:text-left">© {footerYear} DonorsHouse. All rights reserved.</p>
              <p className="text-center sm:text-right">
                Supported by{' '}
                <a
                  href="https://newabilities.org"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="New Abilities Foundation — opens in new tab"
                  className="font-semibold text-red-700 hover:text-red-900 underline underline-offset-2 transition-colors"
                >
                  New Abilities Foundation
                </a>
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;